import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { astrologyChartAPI } from '../services/api';
import { config } from '../config/environment';

interface ChartImportState {
  isImporting: boolean;
  isComplete: boolean;
  charts: any[];
  error: string | null;
}

export const useChartImport = () => {
  const { user } = useAuth();
  const [state, setState] = useState<ChartImportState>({
    isImporting: false,
    isComplete: false,
    charts: [],
    error: null
  });
  const [userBirthData, setUserBirthData] = useState<any>(null);

  // Load user birth data from backend
  const loadUserBirthData = async () => {
    if (!user?.uid) return;
    
    try {
      const response = await fetch(`${config.apiBaseUrl}/api/hybrid-rag/user-profile/${user.uid}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.profile?.birthData) {
          setUserBirthData(data.profile.birthData);
        }
      }
    } catch (error) {
      console.error('Error loading user birth data:', error);
    }
  };

  // Check if user has complete birth data
  const hasCompleteBirthData = (): boolean => {
    if (!userBirthData) return false;
    
    return !!(
      userBirthData.birthDate &&
      userBirthData.birthTime &&
      userBirthData.latitude &&
      userBirthData.longitude
    );
  };

  // Import charts automatically when user has complete birth data
  const importCharts = async () => {
    if (!user || !hasCompleteBirthData()) {
      return;
    }

    setState(prev => ({ ...prev, isImporting: true, error: null }));

    try {
      console.log('Starting automatic chart import for user:', user.uid);
      
      const result = await astrologyChartAPI.importCharts(user.uid, userBirthData);
      
      if (result.success) {
        console.log('Charts imported successfully:', result.charts);
        
        // Get comprehensive analysis
        const analysis = await astrologyChartAPI.getComprehensiveAnalysis(user.uid, userBirthData);
        
        if (analysis.success) {
          console.log('Analysis completed successfully');
        }

        setState(prev => ({
          ...prev,
          isImporting: false,
          isComplete: true,
          charts: result.charts
        }));
      } else {
        throw new Error(result.error || 'Failed to import charts');
      }
    } catch (error: any) {
      console.error('Error importing charts:', error);
      setState(prev => ({
        ...prev,
        isImporting: false,
        error: error.message || 'Failed to import charts'
      }));
    }
  };

  // Load user birth data on mount
  useEffect(() => {
    if (user?.uid) {
      loadUserBirthData();
    }
  }, [user?.uid]);

  // Auto-import charts when user has complete birth data
  useEffect(() => {
    if (user && userBirthData && hasCompleteBirthData() && !state.isComplete && !state.isImporting) {
      // Check if user already has charts in the database first
      checkExistingCharts();
    }
  }, [user, userBirthData]);

  // Check if user already has charts in the database
  const checkExistingCharts = async () => {
    if (!user) return;
    
    try {
      console.log('Checking existing charts for user:', user.uid);
      const result = await astrologyChartAPI.getUserCharts(user.uid);
      console.log('getUserCharts result:', result);
      
      if (result.success && result.charts && Object.keys(result.charts).length > 0) {
        // User already has charts, mark as complete
        console.log('User already has charts in database, skipping import');
        console.log('Charts found:', Object.keys(result.charts));
        setState(prev => ({
          ...prev,
          isComplete: true,
          charts: result.charts
        }));
        return; // Exit early to prevent import
      } else {
        // No charts found, proceed with import
        console.log('No existing charts found, proceeding with import');
        console.log('Result success:', result.success);
        console.log('Result charts:', result.charts);
        console.log('Charts keys:', result.charts ? Object.keys(result.charts) : 'no charts');
        importCharts();
      }
    } catch (error) {
      console.error('Error checking existing charts:', error);
      // If check fails, don't proceed with import - just log the error
      console.log('Error checking charts, not proceeding with import');
      // Don't call importCharts() here - just return
      return;
    }
  };

  // Get user's existing charts
  const getUserCharts = async () => {
    if (!user) return;

    try {
      const result = await astrologyChartAPI.getUserCharts(user.uid);
      
      if (result.success) {
        setState(prev => ({
          ...prev,
          charts: result.charts
        }));
      }
    } catch (error: any) {
      console.error('Error getting user charts:', error);
      setState(prev => ({
        ...prev,
        error: error.message
      }));
    }
  };

  // Manual trigger for chart import
  const triggerImport = () => {
    if (hasCompleteBirthData()) {
      // Check for existing charts first before importing
      checkExistingCharts();
    } else {
      setState(prev => ({
        ...prev,
        error: 'Complete birth data required for chart import'
      }));
    }
  };

  // Reset import state
  const resetImport = () => {
    setState({
      isImporting: false,
      isComplete: false,
      charts: [],
      error: null
    });
  };

  return {
    ...state,
    hasCompleteBirthData,
    importCharts,
    getUserCharts,
    triggerImport,
    resetImport,
    setError: (error: string | null) => setState(prev => ({ ...prev, error }))
  };
}; 